/**
 * @file lorryDay.js
 * @version 1.5.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-12
 * @modified 2026-10-26
 * @description Lorry day planning — the evaluator for the bulk planning board
 *   (behind the transport_day_plan switch). Pure: no database, no React.
 *
 *   1.1.0 (2026-10-12): a row with no weight (a None line, or 0 T) takes no
 *   compartment — never asked for one, placed in one or given one by Suggest
 *   (rowWeightless).
 *
 *   1.2.0 (2026-10-12): a self-billed PO's row (is_self_billed — no order
 *   line, so nowhere to keep a drop time, compartments or a note) is on its
 *   lorry's day as a collect and a drop stop, its drop untimed; it is planned
 *   once it has a vehicle and a collection time, and is never asked for, or
 *   given by Suggest, a compartment (noLinePlan).
 *
 *   1.3.0 (2026-10-13, Cam): "treated like any other order on the board but
 *   flagged as self-billed" — once the database keeps a PO line's plan, a
 *   self-billed row has a drop time, compartments and a note like any row.
 *   Only a row the server stamps `no_line_plan` (before that update) is
 *   still left without them.
 *
 *   1.5.0 (2026-10-26, Cam): a Third-Party Delivery row planned on the board
 *   (third_party_board — a PO-D collected by the haulier linked to its
 *   third-party haulier supplier) is on its lorry's day like any outside
 *   haulier's row; otherCarrierOf names third-party haulage only for a row
 *   that is not.
 *
 *   1.4.0 (2026-10-15, Cam): compartments are WHOLE tonnes, always
 *   ("2T, 3T, never 2.77T") — typed, dragged, suggested, read back
 *   (wholeTonnes). Sizes may add up to MORE than the usable payload ("allow
 *   total over filling … so long as they can see that that is what they have
 *   done"): resizeCompartment changes only the compartment typed, and the
 *   total is a check (compartments-over, warn), never clamped. Each
 *   compartment at a stop says how far it is over (`over`), so the drawing
 *   can cap the fill and flag it. dropTimeAfterCollect: a collection time
 *   typed fills the drop time when it is blank or earlier. lorryOverfill /
 *   overfillWarning name what is over payload for the send windows.
 *
 *   The plan is what the planner enters; this file never moves, re-orders or
 *   re-sizes anything. It works out what follows from the entries and says
 *   what looks wrong, as guidance (Cam, 10 Oct 2026, "The driver's day"
 *   proposal).
 *
 *   Every order line is two STOPS, each with a time:
 *     - COLLECT at the collection time (transport_sequences.scheduled_time,
 *       one per PO). Rows of one supplier at one time are one Collect stop.
 *     - DROP at the drop time (transport.line_plans.drop_time). Rows of one
 *       customer at one address at one time are one Drop stop.
 *   A LORRY's DAY is its stops in time order. Ties: collects before drops;
 *   collects in board order; drops by drop_seq, then board order. A stop
 *   with no time yet goes to the start (collect) or the end (drop).
 *
 *   COMPARTMENTS (C1 nearest the cab) are chosen per line by the planner
 *   (line_plans.compartments). Door LAYOUTS are the lorry-day's: each one
 *   applies from a time until the next (transport.day_plans, version 2):
 *     { version: 2, notes, layouts: [{ from: 'HH:MM' | null, sizes: [front→back] | null, open: [door…] }] }
 *   Door k sits between Ck and Ck+1. Any door may be OPEN (Cam: every door
 *   opens, so one product can fill several compartments). A fixed body
 *   keeps its own sizes; a moveable one takes the layout's.
 *
 *   CHECKS (guidance, never a refusal), each on the stop it concerns:
 *     warn  over the usable payload; product bigger than its compartments;
 *           a compartment already holding another customer's or another
 *           product's load; a compartment the lorry does not have; dropped
 *           before it is collected; a door moved beside a loaded
 *           compartment; a door layout outside the lorry's ranges; a
 *           compartment that carried medicated feed refilled (SWEEP OUT).
 *     info  no compartment chosen yet; on a PART load (the rule does not
 *           apply to a full lorry spread along its body), most of the weight
 *           over the half the rear-lift rule does not want.
 *
 *   Each line may carry a planner's NOTE (line_plans.planner_note): the
 *   planners' own reminder, shown while planning and never printed or sent.
 *
 *   suggestLorryDay fills ONLY what is blank (compartments, and a door
 *   layout for each trip of a moveable body) and never touches an entry.
 *
 *   The server holds a generated copy (utils/lorryDay.js in the parent repo,
 *   written by scripts/sync-shared-utils.js): edit THIS file, then re-run it.
 */

import { usablePayload, moveableRangeConfigs, efficiencyOf } from './vehicleLoad';
import { formatTonnes } from './numberFormatters';
import { isNoWeight } from './lineUnits';
import { isOptionCandidate } from './planningDay';

const EPS = 1e-6;
const r3 = (n) => Math.round(n * 1000) / 1000;
const sumOf = (arr) => arr.reduce((s, v) => s + (Number.isFinite(v) ? v : 0), 0);

export const LORRY_DAY_UNKNOWN_VEHICLE = 'Unknown';
export const LORRY_DAY_VERSION = 2;
const DIRECT_DELIVERY = 'Direct Delivery';
const THIRD_PARTY_DELIVERY = 'Third-Party Delivery';
const SEVERITY_RANK = { muted: 0, ok: 1, info: 2, warn: 3 };

// ── Times ─────────────────────────────────────────────────────────────────

/** 'H:MM', 'HH:MM' or 'HH:MM:SS' → 'HH:MM'; anything else → null. */
export function normaliseTime(t) {
  const m = /^(\d{1,2}):(\d{2})(?::\d{2})?$/.exec(String(t == null ? '' : t).trim());
  if (!m) return null;
  const h = Number(m[1]);
  const mi = Number(m[2]);
  if (h > 23 || mi > 59) return null;
  return `${String(h).padStart(2, '0')}:${m[2]}`;
}

/** Minutes after midnight of 'HH:MM', or null. */
export function minutesOf(t) {
  const n = normaliseTime(t);
  if (!n) return null;
  return Number(n.slice(0, 2)) * 60 + Number(n.slice(3, 5));
}

/** 'HH:MM' from minutes after midnight, clamped to the day. */
export function timeFromMinutes(m) {
  const v = Math.max(0, Math.min(23 * 60 + 59, Math.round(m)));
  return `${String(Math.floor(v / 60)).padStart(2, '0')}:${String(v % 60).padStart(2, '0')}`;
}

// ── Which rows make a lorry's day ─────────────────────────────────────────

// An option-date candidate (planned on another of its option dates) is
// utils/planningDay's isOptionCandidate — the one test the boards, the
// paperwork and the server share.

/** Who carries a row when it is not one of our lorries' (null when it is). */
export function otherCarrierOf(row) {
  if (!row) return null;
  if (row.is_customer_collection) return 'Customer Collection';
  if (String(row.supplier_delivery_terms || '') === DIRECT_DELIVERY) return 'Direct Delivery';
  // Planned on the board with its linked haulier: that haulier's lorry, not "someone else".
  if (String(row.excel_agri_delivery_terms || '') === THIRD_PARTY_DELIVERY && !row.third_party_board) return 'Third-party haulage';
  return null;
}

/**
 * Does this board row belong to a lorry's day? Not when someone else carries
 * it (Customer Collection, Direct Delivery, third-party haulage), not an
 * option-date candidate, and only once it is on a vehicle.
 */
export function inLorryDay(row) {
  if (!row || row.is_haulage_job) return false;
  if (otherCarrierOf(row)) return false;
  if (isOptionCandidate(row)) return false;
  return !!String(row.vehicle || '').trim();
}

/** The lorry a row is on: its registration, or "Unknown::<driver>" (per driver). */
export function lorryKeyOf(row) {
  const reg = String(row?.vehicle || '').trim();
  if (!reg) return null;
  return reg === LORRY_DAY_UNKNOWN_VEHICLE ? `${LORRY_DAY_UNKNOWN_VEHICLE}::${String(row?.driver || '').trim()}` : reg;
}

/** The words for a lorry key: "XYN 670", or "Unknown vehicle (Driver 9)". */
export function lorryLabel(lorryKey) {
  const k = String(lorryKey || '');
  if (k.startsWith(`${LORRY_DAY_UNKNOWN_VEHICLE}::`)) {
    const driver = k.slice(LORRY_DAY_UNKNOWN_VEHICLE.length + 2);
    return `Unknown vehicle (${driver || 'no driver'})`;
  }
  return k;
}

/** The "Unknown" vehicle: a payload set per driver per day, no compartments. */
export function unknownVehicleSpec(payload) {
  return {
    registration: LORRY_DAY_UNKNOWN_VEHICLE,
    payload: (payload == null || payload === '') ? undefined : Number(payload),
    vehicle_type: null, has_moffett: false, has_moveable_doors: false, compartment_count: 0
  };
}

// ── Row keys ──────────────────────────────────────────────────────────────

const normKey = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]/g, '');
const POSTCODE = /\b([A-Z]{1,2}\d[A-Z\d]?)\s*(\d[A-Z]{2})\b/i;
const postcodeIn = (text) => {
  const m = POSTCODE.exec(String(text || ''));
  return m ? `${m[1]}${m[2]}`.toUpperCase() : null;
};
const textOf = (v) => {
  if (v == null) return '';
  if (typeof v === 'string') return v;
  try { return JSON.stringify(v); } catch { return ''; }
};

/**
 * The delivery address a drop is keyed on: the postcode of the PO's delivery
 * address, else the customer's delivery postcode, else their first line.
 */
export function addressKeyOf(row) {
  const fromPo = postcodeIn(textOf(row?.delivery_address));
  if (fromPo) return fromPo;
  const pc = postcodeIn(row?.customer_delivery_postcode);
  if (pc) return pc;
  if (normKey(row?.customer_delivery_postcode)) return normKey(row.customer_delivery_postcode).toUpperCase();
  return normKey(row?.customer_delivery_line1).slice(0, 40);
}

/** A mill's short name: "MVFS - Risdon" → "Risdon". */
export function shortSupplierName(name) {
  const n = String(name || '').trim();
  const mvfs = n.match(/^mvfs\s*-\s*(.+)$/i);
  return mvfs ? mvfs[1].trim() : n;
}

/** "C3", "C3–C4", "C1, C3". */
export function compartmentLabel(nums) {
  const ns = [...new Set((nums || []).map(Number).filter(Number.isFinite))].sort((a, b) => a - b);
  if (!ns.length) return '';
  if (ns.length === 1) return `C${ns[0]}`;
  const contiguous = ns.every((n, i) => i === 0 || n === ns[i - 1] + 1);
  return contiguous ? `C${ns[0]}–C${ns[ns.length - 1]}` : ns.map(n => `C${n}`).join(', ');
}

/**
 * A row that carries no weight — a line set to None (unit weight 0: a
 * service or weighbridge charge, utils/lineUnits) or one of 0 T. It takes no
 * compartment: it is never asked for one, never placed in one, and Suggest
 * leaves it alone (Cam, 2026-10-12). `weightless` is the evaluator's own copy.
 */
export function rowWeightless(row) {
  if (row?.weightless === true) return true;
  if (isNoWeight(row?.unit_weight_kg)) return true;
  if (row?.line_tonnes === null || row?.line_tonnes === undefined || row?.line_tonnes === '') return false;
  const t = Number(row.line_tonnes);
  return Number.isFinite(t) && t === 0;
}

/** A row's tonnes, by the load maths' own rule. */
export function rowTonnes(row) {
  const t = Number(row?.line_tonnes);
  return Number.isFinite(t) ? t : (Number(row?.quantity) || 0);
}

// ── Line plans (transport.line_plans) ─────────────────────────────────────

/** A planner's note: trimmed, at most 500 characters, '' when none. */
export const PLANNER_NOTE_MAX = 500;
export function normaliseNote(v) {
  return String(v == null ? '' : v).replace(/\s+$/g, '').replace(/^\s+/g, '').slice(0, PLANNER_NOTE_MAX);
}

/**
 * One line's plan, canonical: { dropTime, dropSeq, compartments, note }.
 * `note` is the planners' own — shown while planning, never printed or sent.
 */
export function normaliseLinePlan(raw) {
  const r = raw || {};
  const dropTime = normaliseTime(r.dropTime != null ? r.dropTime : r.drop_time);
  const seqRaw = r.dropSeq != null ? r.dropSeq : r.drop_seq;
  const dropSeq = Number.isInteger(Number(seqRaw)) && seqRaw !== null && seqRaw !== '' ? Number(seqRaw) : null;
  const comps = Array.isArray(r.compartments) ? r.compartments : [];
  const compartments = [...new Set(comps.map(Number).filter(n => Number.isInteger(n) && n >= 1 && n <= 20))].sort((a, b) => a - b);
  const note = normaliseNote(r.note != null ? r.note : r.planner_note);
  return { dropTime, dropSeq, compartments, note };
}

// ── The lorry's body ──────────────────────────────────────────────────────

/**
 * What the body is: how many compartments, whether the doors move, the
 * usable payload, and the body's length in tonnes (moveable: the usable
 * payload, whole; fixed: the sum of its compartments).
 */
export function bodyOf(vehicle) {
  const cap = usablePayload(vehicle);
  const moveable = !!vehicle?.has_moveable_doors;
  const ranges = Array.isArray(vehicle?.compartment_ranges) ? vehicle.compartment_ranges : [];
  let count = 0;
  let fixedSizes = null;
  if (moveable) {
    count = ranges.length || Number(vehicle?.compartment_count) || 0;
  } else {
    const caps = (Array.isArray(vehicle?.compartments) ? vehicle.compartments : [])
      .map(c => Number(c?.capacity)).filter(Number.isFinite);
    const n = Number(vehicle?.compartment_count) || caps.length;
    if (n > 0 && caps.length >= n) { count = n; fixedSizes = caps.slice(0, n).map(r3); }
  }
  let length = null;
  if (moveable && cap != null) length = Math.round(cap);
  else if (fixedSizes) length = r3(sumOf(fixedSizes));
  return { count, moveable, cap, length, fixedSizes, ranges, rearLift: !!vehicle?.rear_lift };
}

/** Door positions (tonnes from the cab) for sizes front→back. */
export function doorsOf(sizes) {
  const out = [];
  let c = 0;
  for (let i = 0; i < (sizes || []).length - 1; i++) { c = r3(c + sizes[i]); out.push(c); }
  return out;
}

/** Sizes front→back for door positions over a body `length` tonnes long. */
export function sizesFromDoors(doors, length) {
  const ds = (doors || []).map(Number).filter(Number.isFinite).sort((a, b) => a - b);
  const bounds = [0, ...ds, length];
  const out = [];
  for (let i = 1; i < bounds.length; i++) out.push(r3(bounds[i] - bounds[i - 1]));
  return out;
}

/** Whole-tonne sizes splitting `length` evenly over `count` compartments, the remainder to the cab end. */
function evenSizes(length, count) {
  if (!count || !Number.isFinite(length) || length <= 0) return [];
  const base = Math.floor(length / count);
  const rem = Math.round(length - base * count);
  return Array.from({ length: count }, (_, i) => base + (i < rem ? 1 : 0));
}

/**
 * Sizes a moveable body starts from when nothing is set: an even split, or
 * the first layout its ranges allow when an even split breaks them.
 */
export function defaultSizes(body, vehicle) {
  if (!body || !body.count) return [];
  if (body.fixedSizes) return body.fixedSizes.slice();
  if (body.length == null) return [];
  const even = evenSizes(body.length, body.count);
  if (!layoutRangeProblem(body, even)) return even;
  const cfg = moveableRangeConfigs(vehicle, body.length)[0];
  return cfg ? cfg.sizes.slice().reverse() : even;
}

/**
 * Why sizes do not suit a moveable body (count, ranges), or null. The total
 * is not this question: sizes may add up to more (or less) than the body —
 * layoutTotalCheck says so, as guidance.
 */
export function layoutRangeProblem(body, sizes) {
  if (!body || !body.moveable) return null;
  if (!Array.isArray(sizes) || sizes.length !== body.count) return `the lorry has ${body.count} compartments`;
  for (let i = 0; i < sizes.length; i++) {
    const rg = body.ranges[i];
    if (!rg) continue;
    const mn = Number(rg.min);
    const mx = Number(rg.max);
    if (Number.isFinite(mn) && sizes[i] < mn - EPS) return `C${i + 1} is ${formatTonnes(sizes[i])}, below its ${formatTonnes(mn)} minimum`;
    if (Number.isFinite(mx) && sizes[i] > mx + EPS) return `C${i + 1} is ${formatTonnes(sizes[i])}, above its ${formatTonnes(mx)} maximum`;
  }
  return null;
}

/** A compartment size as the planners enter it: whole tonnes, at least 1 T. */
export function wholeTonnes(n) {
  const v = Number(n);
  if (!Number.isFinite(v)) return null;
  return Math.max(1, Math.round(v));
}

/**
 * What the compartments add up to against the lorry (a moveable body only):
 * more than the usable payload → warn ('compartments-over'); less than the
 * body → info ('compartments-under'); else null. { code, severity, total, by, message }.
 */
export function layoutTotalCheck(body, sizes) {
  if (!body || !body.moveable || !Array.isArray(sizes) || !sizes.length) return null;
  const total = r3(sumOf(sizes));
  if (body.cap != null && total > body.cap + 0.0005) {
    const by = r3(total - body.cap);
    return { code: 'compartments-over', severity: 'warn', total, by,
      message: `The compartments add up to ${formatTonnes(total)} — ${formatTonnes(by)} over the ${formatTonnes(body.cap)} payload` };
  }
  if (body.length != null && total < body.length - 0.0005) {
    const by = r3(body.length - total);
    return { code: 'compartments-under', severity: 'info', total, by,
      message: `The compartments add up to ${formatTonnes(total)} — ${formatTonnes(by)} less than the ${formatTonnes(body.length)} body` };
  }
  return null;
}

/**
 * The drop time a typed collection time fills in (Cam, 2026-10-15): the
 * collection time itself when the drop time is not set or is earlier than
 * it; otherwise null (the drop time stands — the planner may type over it).
 */
export function dropTimeAfterCollect(dropTime, collectTime) {
  const c = minutesOf(collectTime);
  if (c == null) return null;
  const d = minutesOf(dropTime);
  return d == null || d < c ? normaliseTime(collectTime) : null;
}

// ── Lorry-day layouts (transport.day_plans, version 2) ────────────────────

export const emptyLorryDay = () => ({ version: LORRY_DAY_VERSION, notes: '', layouts: [] });

/**
 * Validate and canonicalise a lorry-day (the PUT body, or a stored row).
 * Returns { value } or { error }. A version-1 (Loads and Drops) record is
 * read as empty — that model is gone.
 */
export function normaliseLorryDay(raw) {
  if (raw == null) return { value: emptyLorryDay() };
  if (typeof raw !== 'object' || Array.isArray(raw)) return { error: 'The lorry day must be an object' };
  if (raw.version != null && Number(raw.version) < LORRY_DAY_VERSION) return { value: emptyLorryDay() };
  const out = emptyLorryDay();
  if (raw.notes != null) {
    if (typeof raw.notes !== 'string') return { error: 'Notes must be text' };
    out.notes = raw.notes.slice(0, 2000);
  }
  if (raw.layouts != null) {
    if (!Array.isArray(raw.layouts)) return { error: 'Door layouts must be a list' };
    if (raw.layouts.length > 50) return { error: 'Too many door layouts for one day' };
    const byFrom = new Map();
    for (const l of raw.layouts) {
      if (!l || typeof l !== 'object') return { error: 'A door layout is not readable' };
      const from = l.from == null || l.from === '' ? null : normaliseTime(l.from);
      if (l.from != null && l.from !== '' && !from) return { error: `"${l.from}" is not a time` };
      let sizes = null;
      if (l.sizes != null) {
        if (!Array.isArray(l.sizes) || l.sizes.length > 20) return { error: 'Compartment sizes must be a list' };
        sizes = l.sizes.map(Number);
        if (sizes.some(s => !Number.isFinite(s) || s <= 0 || s > 100)) return { error: 'Each compartment size must be more than 0 T' };
        // Whole tonnes, always (a fraction stored before is read as the
        // nearest whole tonne, and never written again).
        sizes = sizes.map(wholeTonnes);
      }
      const open = Array.isArray(l.open)
        ? [...new Set(l.open.map(Number).filter(n => Number.isInteger(n) && n >= 1 && n <= 19))].sort((a, b) => a - b)
        : [];
      byFrom.set(from == null ? '' : from, { from, sizes, open });
    }
    out.layouts = [...byFrom.values()].sort((a, b) => (minutesOf(a.from) ?? -1) - (minutesOf(b.from) ?? -1));
  }
  return { value: out };
}

/** Are two lorry-days the same (after normalising)? */
export function lorryDaysEqual(a, b) {
  return JSON.stringify(normaliseLorryDay(a).value) === JSON.stringify(normaliseLorryDay(b).value);
}

/** Nothing set: no layouts and no notes. */
export function isEmptyLorryDay(raw) {
  const v = normaliseLorryDay(raw).value;
  return !v || (!v.layouts.length && !v.notes);
}

/**
 * Where a stop sits in the day, in minutes: its time; a collection with no
 * time at the very start (-1), a drop with no time at the very end.
 */
export function stopMinute(stop) {
  const m = minutesOf(stop && stop.time);
  if (m != null) return m;
  return stop && stop.kind === 'collect' ? -1 : 24 * 60 + 1;
}

/**
 * The layout in force at `minute` (stopMinute): { from, sizes front→back,
 * open, set } — `set` false when the lorry's own default is shown because
 * nothing has been chosen. A layout with no time applies from the start.
 */
export function layoutAt(lorryDay, minute, body, vehicle) {
  const layouts = (lorryDay && lorryDay.layouts) || [];
  const at = minute == null ? -1 : minute;
  let chosen = null;
  for (const l of layouts) {
    const m = minutesOf(l.from);
    if (m == null || m <= at) chosen = l;
  }
  const base = defaultSizes(body, vehicle);
  if (!chosen) return { from: null, sizes: base, open: [], set: false };
  const sizes = body && body.fixedSizes ? body.fixedSizes.slice() : (Array.isArray(chosen.sizes) && chosen.sizes.length ? chosen.sizes.slice() : base);
  return { from: chosen.from, sizes, open: (chosen.open || []).filter(d => d < sizes.length), set: true };
}

// ── Planned status ────────────────────────────────────────────────────────

/**
 * Is a row planned? One rule for the board's tick and the counts:
 *   planned  — someone else carries it, or it has a vehicle, a collection
 *              time, a drop time, and compartments where the lorry has them;
 *   part     — on a vehicle, something missing (`missing` names it);
 *   unplanned — no vehicle;
 *   na       — an option-date candidate (planned on another day).
 */
export function rowPlanStatus(row, linePlan, body) {
  if (isOptionCandidate(row)) return { status: 'na', missing: [], carrier: null };
  const carrier = otherCarrierOf(row);
  if (carrier) return { status: 'planned', missing: [], carrier };
  if (!String(row?.vehicle || '').trim()) return { status: 'unplanned', missing: ['vehicle'], carrier: null };
  const lp = normaliseLinePlan(linePlan);
  const missing = [];
  if (!normaliseTime(row.scheduled_time)) missing.push('collection time');
  // A row that can keep no line plan yet (a self-billed PO before the
  // database update): no drop time or compartments asked for.
  if (row?.no_line_plan === true) return { status: missing.length ? 'part' : 'planned', missing, carrier: null };
  if (!lp.dropTime) missing.push('drop time');
  if (body && body.count > 0 && !lp.compartments.length && !rowWeightless(row)) missing.push('compartments');
  return { status: missing.length ? 'part' : 'planned', missing, carrier: null };
}

// ── The day ───────────────────────────────────────────────────────────────

/** A board row as the evaluator sees it. */
export function toDayRow(row, index, linePlan) {
  const lp = normaliseLinePlan(linePlan);
  const supplierName = String(row?.supplier_name || row?.contact_name || row?.supplier_account_no || '').trim();
  return {
    id: String(row?.order_line_id),
    index,
    supplierKey: row?.supplier_account_id != null ? `a${row.supplier_account_id}` : (normKey(supplierName) ? `n${normKey(supplierName)}` : 'none'),
    supplierName,
    collectTime: normaliseTime(row?.scheduled_time),
    customerKey: row?.customer_account_id != null ? `a${row.customer_account_id}` : `n${normKey(row?.customer_name)}`,
    customerName: String(row?.customer_name || '').trim(),
    addressKey: addressKeyOf(row),
    dropTime: lp.dropTime,
    dropSeq: lp.dropSeq,
    compartments: lp.compartments,
    note: lp.note,
    tonnes: rowTonnes(row),
    weightless: rowWeightless(row),
    // A row that can keep no line plan yet (stamped by the server).
    noLinePlan: row?.no_line_plan === true,
    product: row?.product_code || row?.product_name || null,
    productName: String(row?.product_name || row?.product_code || '').trim(),
    medicated: row?.is_medicated === true,
    reference: row?.purchase_order_number || row?.order_number || ''
  };
}

const collectKeyOf = (r) => `C:${r.supplierKey}@${r.collectTime || '-'}`;
const dropKeyOf = (r) => `D:${r.customerKey}|${r.addressKey}@${r.dropTime || '-'}`;

/**
 * The stops of a day, in order. A collect with no time goes first (assumed
 * at the start); a drop with no time goes last.
 */
export function buildStops(dayRows) {
  const stops = new Map();
  for (const r of dayRows) {
    for (const kind of ['collect', 'drop']) {
      const key = kind === 'collect' ? collectKeyOf(r) : dropKeyOf(r);
      if (!stops.has(key)) {
        stops.set(key, {
          key, kind,
          time: kind === 'collect' ? r.collectTime : r.dropTime,
          name: kind === 'collect' ? r.supplierName : r.customerName,
          rowIds: [], firstIndex: r.index, seq: null
        });
      }
      const s = stops.get(key);
      s.rowIds.push(r.id);
      s.firstIndex = Math.min(s.firstIndex, r.index);
      if (kind === 'drop' && r.dropSeq != null) s.seq = s.seq == null ? r.dropSeq : Math.min(s.seq, r.dropSeq);
    }
  }
  const order = (s) => {
    const m = minutesOf(s.time);
    if (m != null) return m;
    return s.kind === 'collect' ? -1 : 24 * 60 + 1;
  };
  return [...stops.values()].sort((a, b) => (order(a) - order(b))
    || ((a.kind === 'collect' ? 0 : 1) - (b.kind === 'collect' ? 0 : 1))
    || ((a.seq ?? Infinity) - (b.seq ?? Infinity))
    || (a.firstIndex - b.firstIndex)
    || a.key.localeCompare(b.key));
}

const check = (code, severity, stop, message, rowId = null, extra = null) => ({
  code, severity, stopKey: stop ? stop.key : null, stopLabel: stop ? stop.label : null, message, rowId, ...(extra || {})
});
const worst = (list) => list.reduce((w, s) => (SEVERITY_RANK[s] > SEVERITY_RANK[w] ? s : w), 'ok');

/** Bounds of each compartment along the body, front→back: [[0, 6], [6, 10], …]. */
const boundsOf = (sizes) => {
  let c = 0;
  return sizes.map(s => { const b = [r3(c), r3(c + s)]; c += s; return b; });
};

/** Front-half and rear-half tonnes of what is on board, by position along the body. */
function halves(sizes, loadByComp) {
  const len = sumOf(sizes);
  const mid = len / 2;
  let front = 0;
  let rear = 0;
  boundsOf(sizes).forEach(([a, b], i) => {
    const w = loadByComp.get(i + 1) || 0;
    if (!w || b <= a) return;
    const f = Math.max(0, Math.min(b, mid) - a) / (b - a);
    front += w * f;
    rear += w * (1 - f);
  });
  return { front: r3(front), rear: r3(rear) };
}

/**
 * One lorry's day, worked out from what was entered.
 *
 * @param {object}   args
 * @param {string}   args.lorryKey
 * @param {object[]} args.rows       the lorry's board rows (inLorryDay)
 * @param {object}   [args.vehicle]  vehicle spec (null: no compartments, no payload)
 * @param {object}   [args.linePlans] { [orderLineId]: { dropTime, dropSeq, compartments } }
 * @param {object}   [args.lorryDay] { layouts, notes } (version 2)
 * @param {number[]} [args.indexes]  each row's position on the whole board
 */
export function buildLorryDay({ lorryKey, rows, vehicle = null, linePlans = {}, lorryDay = null, indexes = null }) {
  const ld = normaliseLorryDay(lorryDay).value || emptyLorryDay();
  const dayRows = (rows || []).map((r, i) => toDayRow(r, indexes ? indexes[i] : i, linePlans ? linePlans[String(r.order_line_id)] : null));
  const rowById = new Map(dayRows.map(r => [r.id, r]));
  const body = bodyOf(vehicle);
  const stops = buildStops(dayRows);
  const first = (rows || [])[0] || {};
  const isUnknown = String(lorryKey || '').startsWith(`${LORRY_DAY_UNKNOWN_VEHICLE}::`);
  const driver = ((rows || []).find(r => r && String(r.driver || '').trim()) || {}).driver || null;

  let collectN = 0;
  let dropN = 0;
  for (const s of stops) {
    if (s.kind === 'collect') { collectN += 1; s.number = collectN; } else { dropN += 1; s.number = dropN; }
    const when = s.time || 'no time';
    s.label = s.kind === 'collect'
      ? `${when} Collect ${shortSupplierName(s.name) || 'supplier'}`
      : `${when} Drop ${s.name || 'customer'}`;
  }
  const stopIndex = new Map(stops.map((s, i) => [s.key, i]));

  // Walk the day.
  const contents = new Map(); // compartment n → [{ rowId, portion }]
  const aboard = new Set();
  const placedIn = new Map(); // rowId → [n]
  const dirty = new Map();    // compartment n → the medicated product it last carried
  const checks = [];
  let layoutPrev = null;
  let peak = { tonnes: 0, stopKey: null, assignment: null };
  const rowOut = {};

  stops.forEach((stop, si) => {
    const stopChecks = [];
    const add = (c) => { stopChecks.push(c); checks.push(c); };
    const layout = body.count ? layoutAt(ld, stopMinute(stop), body, vehicle) : { from: null, sizes: [], open: [], set: false };
    stop.layout = layout;

    if (body.count && layoutPrev && JSON.stringify(layoutPrev.sizes) !== JSON.stringify(layout.sizes)) {
      const before = boundsOf(layoutPrev.sizes);
      const after = boundsOf(layout.sizes);
      const moved = [...contents.keys()].filter(n => (contents.get(n) || []).length
        && (!before[n - 1] || !after[n - 1] || before[n - 1][0] !== after[n - 1][0] || before[n - 1][1] !== after[n - 1][1]));
      if (moved.length) add(check('door-moved', 'warn', stop, `The doors move while ${compartmentLabel(moved)} ${moved.length === 1 ? 'is' : 'are'} loaded`));
    }
    if (body.count && layout.set && body.moveable && (si === 0 || layoutPrev?.from !== layout.from)) {
      const problem = layoutRangeProblem(body, layout.sizes);
      if (problem) add(check('layout-range', 'warn', stop, `Door layout: ${problem}`));
      const total = layoutTotalCheck(body, layout.sizes);
      if (total) add(check(total.code, total.severity, stop, total.message, null, { total: total.total, by: total.by, from: layout.from }));
    }
    layoutPrev = layout;

    if (stop.kind === 'collect') {
      for (const id of stop.rowIds) {
        const r = rowById.get(id);
        aboard.add(id);
        const who = `${r.customerName || r.reference}${r.productName ? ` (${r.productName})` : ''}`;
        // Nothing to load: no compartment, no checks about one.
        if (!body.count || r.weightless || r.noLinePlan) continue;
        const comps = r.compartments.filter(n => n <= body.count);
        const missing = r.compartments.filter(n => n > body.count);
        if (missing.length) add(check('compartment-missing', 'warn', stop, `${who}: this lorry has no ${missing.map(n => `C${n}`).join(', ')}`, id));
        if (!comps.length) {
          if (!missing.length) add(check('not-placed', 'info', stop, `${who}: no compartment chosen yet`, id));
          continue;
        }
        const room = sumOf(comps.map(n => layout.sizes[n - 1] || 0));
        if (r.tonnes > room + 0.0005) {
          add(check('compartment-size', 'warn', stop, `${who}: ${formatTonnes(r.tonnes)} will not go in ${compartmentLabel(comps)} (${formatTonnes(r3(room))})`, id));
        }
        for (const n of comps) {
          const there = (contents.get(n) || []).map(c => rowById.get(c.rowId)).filter(Boolean);
          if (there.length) {
            const otherProduct = there.some(o => o.product !== r.product);
            const otherCustomer = there.some(o => o.customerKey !== r.customerKey);
            if (otherProduct) add(check('compartment-taken', 'warn', stop, `${who}: C${n} still holds ${there[0].customerName || there[0].reference}'s ${there[0].productName || 'load'}`, id));
            else if (otherCustomer) add(check('shared', 'warn', stop, `${who}: C${n} is shared with ${there[0].customerName || there[0].reference}'s ${there[0].productName || 'load'} (same product)`, id));
          }
          if (dirty.has(n) && dirty.get(n) !== r.product) {
            add(check('sweep', 'warn', stop, `Sweep out C${n} before loading ${who} — it carried medicated feed`, id));
            stop.sweep = [...(stop.sweep || []), n];
            dirty.delete(n);
          } else if (dirty.has(n)) {
            dirty.delete(n);
          }
        }
        // Spread by size, so the weight sits where the product does.
        const sizes = comps.map(n => layout.sizes[n - 1] || 0);
        const total = sumOf(sizes) || comps.length;
        comps.forEach((n, i) => {
          const portion = r3(r.tonnes * ((sumOf(sizes) ? sizes[i] : 1) / total));
          contents.set(n, [...(contents.get(n) || []), { rowId: id, portion }]);
        });
        placedIn.set(id, comps);
      }
    } else {
      for (const id of stop.rowIds) {
        const r = rowById.get(id);
        if (!aboard.has(id)) {
          const collect = stopIndex.get(collectKeyOf(r));
          if (collect != null && collect > si) {
            add(check('drop-before-collect', 'warn', stop, `${r.customerName || r.reference}${r.productName ? ` (${r.productName})` : ''} is dropped before it is collected`, id));
          }
          continue;
        }
        aboard.delete(id);
        for (const n of placedIn.get(id) || []) {
          contents.set(n, (contents.get(n) || []).filter(c => c.rowId !== id));
          if (r.medicated) dirty.set(n, r.product);
        }
        placedIn.delete(id);
      }
    }

    const onBoard = r3(sumOf([...aboard].map(id => rowById.get(id).tonnes)));
    stop.onBoard = onBoard;
    if (stop.kind === 'collect' && body.cap != null && onBoard > body.cap + 0.0005) {
      add(check('overweight', 'warn', stop, `${formatTonnes(onBoard)} on board — ${formatTonnes(r3(onBoard - body.cap))} over the ${formatTonnes(body.cap)} payload`,
        null, { total: onBoard, by: r3(onBoard - body.cap) }));
    }
    const loadByComp = new Map([...contents.entries()].map(([n, items]) => [n, sumOf(items.map(i => i.portion))]));
    if (stop.kind === 'collect' && body.count && body.cap != null && onBoard > EPS && onBoard < body.cap - 0.0005) {
      const unplaced = [...aboard].some(id => !placedIn.has(id));
      if (!unplaced) {
        const h = halves(layout.sizes, loadByComp);
        const wantFront = body.rearLift;
        const wrong = wantFront ? h.rear > h.front + 0.05 : h.front > h.rear + 0.05;
        if (wrong) {
          add(check('balance', 'info', stop,
            `Part load: ${formatTonnes(h.front)} over the front half, ${formatTonnes(h.rear)} over the rear${wantFront ? ' — with a rear lift the front should carry more' : ' — without a rear lift the rear should carry more'}`));
        }
      }
    }
    stop.compartments = body.count ? layout.sizes.map((size, i) => {
      const n = i + 1;
      const items = (contents.get(n) || []).map(c => ({ ...c }));
      const load = r3(sumOf(items.map(it => it.portion)));
      // How far the compartment is over its size (0 when it is not) — the
      // drawing caps the fill at the compartment and flags it.
      const over = load > size + 0.0005 ? r3(load - size) : 0;
      return { n, size, items, dirty: dirty.has(n), over };
    }) : [];
    if (stop.kind === 'collect' && onBoard >= peak.tonnes - EPS && onBoard > EPS) {
      peak = {
        tonnes: onBoard, stopKey: stop.key,
        assignment: body.count ? stop.compartments.map(c => {
          const portion = r3(sumOf(c.items.map(i => i.portion)));
          return { size: c.size, portion, fill: c.size > 0 && portion > EPS ? Math.min(portion / c.size, 1) : null };
        }) : null
      };
    }
    stop.checks = stopChecks;
    stop.severity = worst(stopChecks.map(c => c.severity));
  });

  const dropStops = stops.filter(s => s.kind === 'drop');
  for (const r of dayRows) {
    const ds = stops.find(s => s.key === dropKeyOf(r));
    const status = rowPlanStatus({ scheduled_time: r.collectTime, vehicle: 'x', weightless: r.weightless, no_line_plan: r.noLinePlan },
      { dropTime: r.dropTime, compartments: r.compartments }, body);
    rowOut[r.id] = {
      id: r.id,
      collectKey: collectKeyOf(r), dropKey: dropKeyOf(r),
      collectTime: r.collectTime, dropTime: r.dropTime, dropSeq: r.dropSeq,
      compartments: r.compartments, compartmentLabel: compartmentLabel(r.compartments),
      dropNumber: ds ? ds.number : null, dropCount: dropStops.length,
      tonnes: r.tonnes, customerName: r.customerName, productName: r.productName,
      supplierName: r.supplierName, medicated: r.medicated, product: r.product, note: r.note,
      weightless: r.weightless,
      noLinePlan: r.noLinePlan,
      status: status.status, missing: status.missing
    };
  }

  const eff = efficiencyOf(peak.assignment, peak.tonnes, body.cap);
  const severity = !vehicle ? 'muted' : worst(checks.map(c => c.severity));
  return {
    lorryKey,
    registration: isUnknown ? LORRY_DAY_UNKNOWN_VEHICLE : (vehicle?.registration || String(first.vehicle || '').trim() || lorryKey),
    label: lorryLabel(lorryKey),
    driver,
    haulier: ((rows || []).find(r => r && String(r.haulier || '').trim()) || {}).haulier || null,
    isUnknown,
    body,
    lorryDay: ld,
    stops: stops.map(s => { const out = { ...s }; delete out.firstIndex; return out; }),
    rows: rowOut,
    rowOrder: dayRows.map(r => r.id),
    checks,
    severity,
    peak: { tonnes: peak.tonnes, stopKey: peak.stopKey },
    efficiency: eff.efficiency,
    payloadUtil: eff.payloadUtil,
    compartmentFill: eff.compartmentFill,
    collectCount: collectN,
    dropCount: dropN,
    summary: stops.map(s => (s.kind === 'collect'
      ? `${s.time || '—'} ${shortSupplierName(s.name)}`
      : `${s.time || '—'} ${s.name}`)).join(' › ')
  };
}

/**
 * Every lorry's day from the board's rows (in board order).
 * @param {object}   args
 * @param {object[]} args.rows           all board rows
 * @param {Function} args.vehicleFor     (lorryKey, { registration, driver }) → spec | null
 * @param {object}   [args.linePlans]    { [orderLineId]: plan }
 * @param {Function} [args.lorryDayFor]  lorryKey → { layouts, notes } | null
 */
export function buildLorryDays({ rows, vehicleFor, linePlans = {}, lorryDayFor = () => null }) {
  const groups = new Map();
  (rows || []).forEach((r, i) => {
    if (!inLorryDay(r)) return;
    const key = lorryKeyOf(r);
    if (!groups.has(key)) groups.set(key, { rows: [], indexes: [] });
    groups.get(key).rows.push(r);
    groups.get(key).indexes.push(i);
  });
  const out = [];
  for (const [lorryKey, g] of groups) {
    const reg = String(g.rows[0].vehicle || '').trim();
    const driver = String(g.rows[0].driver || '').trim();
    out.push(buildLorryDay({
      lorryKey, rows: g.rows, indexes: g.indexes,
      vehicle: vehicleFor ? vehicleFor(lorryKey, { registration: reg, driver }) : null,
      linePlans, lorryDay: lorryDayFor(lorryKey)
    }));
  }
  return out;
}

/** Row → its place in its lorry's day: { lorryKey, compartments, dropNumber, … }. */
export function rowPlanMap(days) {
  const map = new Map();
  for (const day of days || []) {
    for (const [id, r] of Object.entries(day.rows || {})) map.set(String(id), { ...r, lorryKey: day.lorryKey });
  }
  return map;
}

/** The worst severity of a list of days ('ok' when none). */
export function worstSeverity(days) {
  return worst((days || []).map(d => d.severity || 'ok'));
}

// ── Over-filled (guidance, never a refusal) ───────────────────────────────

const OVERFILL_CODES = new Set(['compartments-over', 'overweight']);

/**
 * What is over the payload on one lorry's day: its door layouts whose sizes
 * add up to more than the payload ({ from, total, by }), and the most on
 * board above it ({ stopLabel, total, by } at the worst stop). Empty lists /
 * null when nothing is.
 */
export function lorryOverfill(day) {
  const checks = (day && day.checks) || [];
  const layouts = [];
  const seen = new Set();
  for (const c of checks) {
    if (c.code !== 'compartments-over') continue;
    const k = `${c.from || ''}|${c.total}`;
    if (seen.has(k)) continue;
    seen.add(k);
    layouts.push({ from: c.from || null, total: c.total, by: c.by });
  }
  let onBoard = null;
  for (const c of checks) {
    if (c.code === 'overweight' && (!onBoard || c.by > onBoard.by)) onBoard = { stopLabel: c.stopLabel, total: c.total, by: c.by };
  }
  return { layouts, onBoard, any: layouts.length > 0 || !!onBoard };
}

/** Does a day carry an over-filled check? */
export const isOverfillCheck = (c) => !!c && OVERFILL_CODES.has(c.code);

/**
 * One sentence for the send windows naming every lorry that is over its
 * payload and by how much, or null when none is (Cam, 2026-10-15: "so long as
 * they can see that that is what they have done before finalising their plan
 * and sending it out"). Never stops a send.
 */
export function overfillWarning(days) {
  const parts = [];
  for (const d of days || []) {
    const o = lorryOverfill(d);
    if (!o.any) continue;
    const cap = d.body && d.body.cap != null ? formatTonnes(d.body.cap) : null;
    const bits = [];
    for (const l of o.layouts) {
      bits.push(`compartments add up to ${formatTonnes(l.total)}, ${formatTonnes(l.by)} over${cap ? ` its ${cap} payload` : ''}${l.from ? ` (from ${l.from})` : ''}`);
    }
    if (o.onBoard) {
      bits.push(`${formatTonnes(o.onBoard.total)} on board${o.onBoard.stopLabel ? ` after ${o.onBoard.stopLabel}` : ''}, ${formatTonnes(o.onBoard.by)} over${cap ? ` its ${cap} payload` : ''}`);
    }
    parts.push(`${d.label || d.lorryKey}: ${bits.join('; ')}`);
  }
  if (!parts.length) return null;
  return `Over payload — ${parts.join(' · ')}. Check this is what you meant before sending.`;
}

// ── Editing helpers (the Lorry day window) ────────────────────────────────

/**
 * The time a stop takes when it is moved between `prev` and `next` (stops,
 * either may be missing or untimed): halfway when there is room, the
 * neighbour's own time when there is not. { time, tie } — tie is the stop it
 * now shares a time with (its order then comes from drop_seq).
 */
export function timeBetween(prev, next) {
  const p = prev ? minutesOf(prev.time) : null;
  const n = next ? minutesOf(next.time) : null;
  if (p != null && n != null) {
    if (n - p >= 2) {
      let m = p + Math.floor((n - p) / 2);
      const five = Math.round(m / 5) * 5;
      if (five > p && five < n) m = five;
      return { time: timeFromMinutes(m), tie: null };
    }
    return { time: timeFromMinutes(p), tie: prev };
  }
  if (p != null) return { time: timeFromMinutes(p + 30), tie: null };
  if (n != null) return { time: timeFromMinutes(Math.max(0, n - 15)), tie: null };
  return { time: null, tie: null };
}

/**
 * Set door `door` (between C`door` and C`door`+1) to `position` tonnes from
 * the cab, whole tonnes, kept at least 1 T from its neighbours. Sizes front→back.
 */
export function moveDoor(sizes, door, position) {
  const doors = doorsOf(sizes);
  const length = sumOf(sizes);
  if (door < 1 || door > doors.length) return sizes.slice();
  const lo = door === 1 ? 1 : doors[door - 2] + 1;
  const hi = door === doors.length ? length - 1 : doors[door] - 1;
  doors[door - 1] = Math.max(lo, Math.min(hi, Math.round(position)));
  return sizesFromDoors(doors, length);
}

/**
 * Set compartment `n`'s size, in whole tonnes (at least 1 T). Only that
 * compartment changes: the sizes may then add up to more — or less — than the
 * lorry's payload (Cam, 2026-10-15: "allow total over filling if the user
 * wants"); layoutTotalCheck says so on the plan, nothing clamps it.
 */
export function resizeCompartment(sizes, n, size) {
  const out = sizes.slice();
  const i = n - 1;
  if (i < 0 || i >= out.length) return out;
  const want = wholeTonnes(size);
  if (want == null) return out;
  out[i] = want;
  return out;
}

// ── Suggest (fills blanks only) ───────────────────────────────────────────

/** Place rows rear-first into free compartments; returns { placed: Map, failed: [] , used }. */
function placeRearFirst(sizes, taken, rowsInOrder) {
  const n = sizes.length;
  const used = new Set(taken);
  const placed = new Map();
  const failed = [];
  let usedSize = 0;
  let usedCount = 0;
  for (const r of rowsInOrder) {
    // Contiguous run from the rear-most free compartment towards the cab.
    let got = null;
    for (let start = n; start >= 1 && !got; start--) {
      if (used.has(start)) continue;
      const run = [];
      let room = 0;
      for (let c = start; c >= 1 && !used.has(c); c--) {
        run.push(c);
        room += sizes[c - 1];
        if (room + 0.0005 >= r.tonnes) break;
      }
      if (room + 0.0005 >= r.tonnes) got = run;
    }
    if (!got) { failed.push(r); continue; }
    got.forEach(c => { used.add(c); usedSize += sizes[c - 1]; });
    usedCount += got.length;
    placed.set(r.id, got.sort((a, b) => a - b));
  }
  return { placed, failed, usedSize: r3(usedSize), usedCount };
}

/** Doors inside a row that spans compartments: opened. */
const openDoorsFor = (placed) => {
  const open = new Set();
  for (const comps of placed.values()) {
    for (let i = 0; i < comps.length - 1; i++) if (comps[i + 1] === comps[i] + 1) open.add(comps[i]);
  }
  return [...open].sort((a, b) => a - b);
};

/**
 * Fill what is blank: compartments for rows without any, and, on a moveable
 * body, a door layout at the start of each trip (the lorry empty, then
 * loaded) that has none of its own. Rows dropped first go to the rear;
 * of rows dropped together, the one collected later goes nearer the rear.
 * Nothing the planner set is changed.
 *
 * @returns {{ linePlans: object, lorryDay: object, filled: { compartments: number, layouts: number } }}
 */
export function suggestLorryDay({ lorryKey, rows, vehicle, linePlans = {}, lorryDay = null }) {
  const plans = {};
  for (const r of rows || []) plans[String(r.order_line_id)] = normaliseLinePlan(linePlans[String(r.order_line_id)]);
  const ld = normaliseLorryDay(lorryDay).value || emptyLorryDay();
  const body = bodyOf(vehicle);
  const filled = { compartments: 0, layouts: 0 };
  if (!body.count) return { linePlans: plans, lorryDay: ld, filled };

  const day = buildLorryDay({ lorryKey, rows, vehicle, linePlans: plans, lorryDay: ld });
  const stopIdx = new Map(day.stops.map((s, i) => [s.key, i]));
  const rowInfo = day.rows;

  // Trips: from a collect onto an empty lorry until it is empty again.
  const trips = [];
  let current = null;
  let aboard = 0;
  let lastEmptyAt = null; // minute the lorry last became empty
  day.stops.forEach((s, i) => {
    if (s.kind === 'collect' && aboard === 0) {
      current = { start: i, stops: [], afterEmpty: lastEmptyAt };
      trips.push(current);
    }
    if (current) current.stops.push(i);
    aboard += s.kind === 'collect' ? s.rowIds.length : -s.rowIds.filter(id => {
      const ci = stopIdx.get(rowInfo[id].collectKey);
      return ci != null && ci < i;
    }).length;
    if (aboard <= 0) { aboard = 0; lastEmptyAt = stopMinute(s); current = null; }
  });

  for (const trip of trips) {
    const startStop = day.stops[trip.start];
    const startMin = stopMinute(startStop);
    // The first wave: everything collected before the trip's first drop.
    const firstDrop = trip.stops.find(i => day.stops[i].kind === 'drop');
    const waveStops = trip.stops.filter(i => day.stops[i].kind === 'collect' && (firstDrop == null || i < firstDrop));
    const later = trip.stops.filter(i => day.stops[i].kind === 'collect' && firstDrop != null && i > firstDrop);
    const waveRows = waveStops.flatMap(i => day.stops[i].rowIds.map(id => ({ ...rowInfo[id], collectIdx: i })));
    const dropIdx = (r) => stopIdx.get(r.dropKey) ?? Infinity;
    const ordered = waveRows.slice().sort((a, b) => (dropIdx(a) - dropIdx(b)) || (b.collectIdx - a.collectIdx));
    const pinned = ordered.filter(r => plans[r.id].compartments.length);
    const free = ordered.filter(r => !plans[r.id].compartments.length && !r.weightless && !r.noLinePlan);

    // A layout of the trip's own: set during the empty gap before it, or at
    // its start. One from the start of the day (no time) is the first trip's.
    const own = ld.layouts.find(l => {
      const m = minutesOf(l.from);
      if (m == null) return trip.afterEmpty == null;
      return m <= startMin && (trip.afterEmpty == null || m >= trip.afterEmpty);
    });
    let sizes = layoutAt(ld, startMin, body, vehicle).sizes;
    if (body.moveable && !own && body.length != null && (startStop.time || trip.afterEmpty == null)) {
      const taken = pinned.flatMap(r => plans[r.id].compartments);
      let best = null;
      for (const cfg of moveableRangeConfigs(vehicle, body.length)) {
        const f2b = cfg.sizes.slice().reverse();
        const pinnedFit = pinned.every(r => r.tonnes <= sumOf(plans[r.id].compartments.map(n => f2b[n - 1] || 0)) + 0.0005);
        if (!pinnedFit) continue;
        const res = placeRearFirst(f2b, taken, free);
        const tonnes = sumOf(free.filter(r => res.placed.has(r.id)).map(r => r.tonnes));
        const score = [res.failed.length, res.usedCount, r3(res.usedSize - tonnes)];
        if (!best || score[0] < best.score[0] || (score[0] === best.score[0] && (score[1] < best.score[1]
          || (score[1] === best.score[1] && score[2] < best.score[2] - EPS)))) {
          best = { f2b, res, score };
        }
      }
      if (best) {
        sizes = best.f2b;
        const allPlaced = new Map([...pinned.map(r => [r.id, plans[r.id].compartments]), ...best.res.placed]);
        ld.layouts = [...ld.layouts.filter(l => l.from !== startStop.time), { from: startStop.time, sizes, open: openDoorsFor(allPlaced) }]
          .sort((a, b) => (minutesOf(a.from) ?? -1) - (minutesOf(b.from) ?? -1));
        filled.layouts += 1;
      }
    }
    const taken = pinned.flatMap(r => plans[r.id].compartments);
    const res = placeRearFirst(sizes, taken, free);
    for (const [id, comps] of res.placed) { plans[id].compartments = comps; filled.compartments += 1; }

    // Top-ups later in the trip: free compartments, as they stand then.
    for (const ci of later) {
      const now = buildLorryDay({ lorryKey, rows, vehicle, linePlans: plans, lorryDay: ld });
      const stop = now.stops[ci];
      const before = now.stops[ci - 1];
      const busy = (before ? before.compartments : []).filter(c => c.items.length).map(c => c.n);
      const want = stop.rowIds.map(id => now.rows[id]).filter(r => !plans[r.id].compartments.length && !r.weightless && !r.noLinePlan)
        .sort((a, b) => (stopIdx.get(a.dropKey) ?? Infinity) - (stopIdx.get(b.dropKey) ?? Infinity));
      const pinnedHere = stop.rowIds.flatMap(id => plans[id].compartments);
      const put = placeRearFirst(stop.layout.sizes, [...busy, ...pinnedHere], want);
      for (const [id, comps] of put.placed) { plans[id].compartments = comps; filled.compartments += 1; }
    }
  }
  return { linePlans: plans, lorryDay: ld, filled };
}
